import DesktopUserArticleCard from './DesktopUserArticleCard'
import MobileUserArticleCard from './MobileUserArticleCard'
import React, { useRef, useEffect } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import tw, { theme } from 'twin.macro'
import { DesktopDisplayContainer, MobileDisplayContainer } from '@components/DeviceContainer'
import Loading from '@components/Loading'
import { getIndexUserPostList } from '@app-core/profile/actions'
import { useDispatch } from 'react-redux'
import { PlusIcon } from '@components/Icons'
import appUrlLib from '@libs/appUrl'

const LinkWithRnAppWebview = ({ children, ...props }) => {
  const handleOnClick = (e) => {
    e.preventDefault()
    if (window.RNApp && window.RNApp.webview) {
      window.RNApp.webview(props.href)
    } else {
      window.open(props.href)
    }
  }

  return (
    <a {...props} onClick={handleOnClick}>
      {children}
    </a>
  )
}

const DesktopGridContainer = styled.div`
  margin-top: 24px;
  ${tw`grid grid-cols-2`}
  gap: 26px 32px;
`

const MobileGridContainer = styled.div`
  ${tw`grid grid-cols-1 gap-6`}
`

const LoadingContainer = styled.div`
  padding: 40px 0;
`

const IntersectionObserverTarget = styled.div`
  position: absolute;
  bottom: 0px;
  right: 0px;
  width: 100px;
  height: 50vh;
  z-index: -100000;
  border: 1px solid black;
`

const StatusHintContainer = styled.div`
  margin-top: 38px;
  text-align: center;
  @media (min-width: ${theme`screens.md`}) {
    margin-top: 60px;
  }
`

const StatusHintImage = styled.img`
  width: 180px;
  height: 180px;
  margin: 0 auto;
`

const StatusHintText = styled.div`
  ${tw`text-h5 text-black font-normal`}
  margin: 0 auto;
  margin-top: 20px;
  margin-bottom: 32px;
  line-height: 24px;
`

interface StatusHintButtonProps {
  isprimary?: boolean
}

const StatusHintButton = styled(LinkWithRnAppWebview)<StatusHintButtonProps>`
  display: block;
  ${tw`text-h5 font-medium bg-primary-indigo`}
  ${(props: any) =>
    props.isprimary ? tw`text-white bg-primary-indigo` : tw`text-black bg-gray-30`}
  margin: 0 auto;
  height: 56px;
  line-height: 56px;
  text-align: center;
  width: 240px;
  border-radius: 28px;
`

const AddNewArticleIconContainer = styled(LinkWithRnAppWebview)`
  position: fixed;
  right: 20px;
  bottom: 36px;
  width: 52px;
  height: 52px;
  border-radius: 26px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(315deg, #6666ff 16.35%, #b366ff 84.62%);
  cursor: pointer;
  z-index: 1000;
`

const StyledPlusIcon = styled(PlusIcon)`
  width: 24px;
  height: 24px;
  color: white;
`

const ArticleCards = () => {
  const posts = useSelector<any, any>((state) => state.profile.getIn(['userPosts']))
  const isGuest = useSelector<any, any>((state) => state.profile.getIn(['isGuest']))
  const isMeRoute = useSelector<any, any>((state) => state.profile.getIn(['isMeRoute']))
  const isSelf = useSelector<any, any>((state) => state.profile.getIn(['isSelf']))
  const roles = useSelector<any, any>((state) => state.profile.getIn(['userInfo', 'data', 'roles']))
  const isNotCreatorOrAdmin = !(roles.toJS().includes('CREATOR') || roles.toJS().includes('ADMIN'))
  const intersectionObserverTargetRef = useRef(null)
  const dispatch = useDispatch()
  // 利用 ref 的方式儲存最新的 posts
  const currentPosts = useRef<any>(null)
  const articles = posts.getIn(['list']).toJS()
  const isLoading = posts.getIn(['isLoading'], false)

  const fetchMore = () => {
    if (!currentPosts.current) return
    const hasMore = currentPosts.current.getIn(['hasMore'], true)
    const isLoading = currentPosts.current.getIn(['isLoading'], false)
    if (isLoading || !hasMore) return

    const type = currentPosts.current.getIn(['type'])
    const clas = currentPosts.current.getIn(['clas'])
    const mode = currentPosts.current.getIn(['mode'])
    const page = currentPosts.current.getIn(['page'])
    const score = currentPosts.current.getIn(['score'])
    const touch = currentPosts.current.getIn(['currentList']).toJS()
    dispatch(getIndexUserPostList({ type, clas, mode, page, score, touch }))
  }

  const handleCreateIntersectionObserver = () => {
    if (intersectionObserverTargetRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]
          if (!entry.isIntersecting) {
            return
          }
          fetchMore()
        },
        {
          threshold: [0, 0.25, 0.5, 0.75, 1],
        },
      )
      observer.observe(intersectionObserverTargetRef.current)
      return observer
    }
  }

  useEffect(() => {
    const intersectionObserver = handleCreateIntersectionObserver()
    return () => intersectionObserver.disconnect()
  }, [])

  useEffect(() => {
    // 隨時更新最新的 posts 到 currentPosts，讓 fetchMore 可以使用
    currentPosts.current = posts
  })

  if ((isSelf || isMeRoute) && isGuest) {
    return (
      <StatusHintContainer>
        <StatusHintImage src="/statics/images/profile/Prompt_App.svg" />
        <StatusHintText>
          <div>你尚未登入喔！</div>
          <div>立即加入波波宇宙</div>
        </StatusHintText>
        <StatusHintButton isprimary href={`${appUrlLib.appUrl}/login`}>
          註冊／登入
        </StatusHintButton>
      </StatusHintContainer>
    )
  }

  if ((isSelf || isMeRoute) && isNotCreatorOrAdmin) {
    return (
      <StatusHintContainer>
        <StatusHintImage src="/statics/images/profile/Empty_CreatTopic.svg" />
        <StatusHintText>
          <div>想在 PopDaily 發文嗎？</div>
          <div>立即加入創作者計畫</div>
        </StatusHintText>
        <StatusHintButton href={`${appUrlLib.appUrl}/poper`}>了解更多</StatusHintButton>
      </StatusHintContainer>
    )
  }

  if ((isSelf || isMeRoute) && !articles.length) {
    return (
      <StatusHintContainer>
        <StatusHintImage src="/statics/images/profile/Prompt_Welcome.svg" />
        <StatusHintText>
          <div>尚無文章</div>
        </StatusHintText>
        <StatusHintButton href={`${appUrlLib.appUrl}/articles/new`}>寫新文章</StatusHintButton>
      </StatusHintContainer>
    )
  }

  if (!isMeRoute && !articles.length) {
    return (
      <StatusHintContainer>
        <StatusHintImage src="/statics/images/profile/Prompt_Welcome.svg" />
        <StatusHintText>
          <div>尚無文章</div>
        </StatusHintText>
      </StatusHintContainer>
    )
  }

  return (
    <div tw="relative">
      <DesktopDisplayContainer>
        <DesktopGridContainer>
          {articles.map((article) => {
            return <DesktopUserArticleCard key={article.postID} article={article} />
          })}
        </DesktopGridContainer>
      </DesktopDisplayContainer>
      <MobileDisplayContainer>
        <MobileGridContainer>
          {articles.map((article, index) => {
            return <MobileUserArticleCard key={article.postID} article={article} index={index} />
          })}
        </MobileGridContainer>
        {(isSelf || isMeRoute) && articles.length && !isNotCreatorOrAdmin && (
          <AddNewArticleIconContainer href={`${appUrlLib.appUrl}/articles/new`}>
            <StyledPlusIcon />
          </AddNewArticleIconContainer>
        )}
      </MobileDisplayContainer>
      <IntersectionObserverTarget ref={intersectionObserverTargetRef} />
      {isLoading && (
        <LoadingContainer>
          <Loading />
        </LoadingContainer>
      )}
    </div>
  )
}

export default ArticleCards
